import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TestimonialsContainer = styled.section`
  padding: 120px 5% 80px;
  background: #000000;
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const TestimonialsContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: left;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 700;
  color: white;
  margin: 0 0 16px 0;
  letter-spacing: -0.02em;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  max-width: 600px;
  line-height: 1.6;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 32px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.15);
    transform: translateY(-4px);
  }
`;

const TestimonialQuote = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0 0 24px 0;
  font-style: italic;
  
  &:before {
    content: '"';
    font-size: 24px;
    color: #FF2300;
    font-weight: 700;
  }
  
  &:after {
    content: '"';
    font-size: 24px;
    color: #FF2300;
    font-weight: 700;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AuthorAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #FF2300 0%, #FF6B35 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: white;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AuthorName = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const AuthorTitle = styled.span`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
`;

const LogosSection = styled.div`
  text-align: left;
`;

const LogosTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 32px 0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const LogosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 32px;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const LogoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.8);
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  margin-top: 80px;
  padding: 40px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const StatItem = styled.div`
  text-align: left;
`;

const StatNumber = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 36px;
  font-weight: 800;
  color: #FF2300;
  margin-bottom: 8px;
  letter-spacing: -0.02em;
`;

const StatLabel = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
`;

const Testimonials = () => {
  const testimonials = [
    {
      quote: "DIZEE transformed how I understand my music business. The AI insights helped me identify my most valuable fans and optimize my tour routing. Revenue increased 40% in just 6 months.",
      author: "Sarah Chen",
      title: "Independent Artist",
      avatar: "SC"
    },
    {
      quote: "Managing 15+ artists used to be chaos. DIZEE's unified dashboard gives me real-time insights across all my clients. The automated reporting alone saves me 20 hours a week.",
      author: "Marcus Rodriguez",
      title: "Artist Manager, Velocity Music",
      avatar: "MR"
    },
    {
      quote: "The playlist tracking and AI-powered pitch suggestions have been game-changing. We've seen a 3x increase in playlist placements since implementing DIZEE's recommendations.",
      author: "Emma Thompson",
      title: "A&R Director, Atlantic Records",
      avatar: "ET"
    },
    {
      quote: "DIZEE's revenue optimization tools helped us restructure our streaming strategy. The predictive analytics showed us exactly where to focus our marketing spend for maximum ROI.",
      author: "David Park",
      title: "CEO, Indie Collective",
      avatar: "DP"
    },
    {
      quote: "The fan engagement analytics are incredible. Understanding which content resonates most with our audience has completely changed our social media strategy and doubled our engagement rates.",
      author: "Lisa Johnson",
      title: "Marketing Director, Rising Star Records",
      avatar: "LJ"
    },
    {
      quote: "As a label with 50+ artists, DIZEE's multi-artist dashboards and team collaboration features are essential. The white-label reports impress our artists and investors.",
      author: "Alex Kim",
      title: "VP of Analytics, Future Sound",
      avatar: "AK"
    }
  ];

  const clientLogos = [
    "Atlantic Records",
    "Sony Music",
    "Universal",
    "Warner Music",
    "Spotify",
    "Apple Music",
    "Live Nation",
    "Ticketmaster",
    "Bandcamp",
    "SoundCloud",
    "Tidal",
    "YouTube Music"
  ];

  const stats = [
    { number: "10K+", label: "Active Artists" },
    { number: "50M+", label: "Tracks Analyzed" },
    { number: "95%", label: "Customer Satisfaction" },
    { number: "40%", label: "Average Revenue Increase" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return null;
};

export default Testimonials; 